import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../config';

// #64 — Onboarding Interview overlay. Drives the deterministic /api/interview engine (start → answer →
// confirm → done). Most slots take free text; the "priorities" slot is a multi-select over the launcher
// catalog and the "assistant_setup" slot is name+avatar pickers (both submitted as text → resolved +
// validated server-side). Skippable, with a "don't show again" that dismisses permanently.
const NAVY = '#0f1b2d', NAVY2 = '#16263f', GOLD = '#c8a951', GOLD_SOFT = '#f2e9cf';

export default function OnboardingInterview({ fetchWithAuth, offer = {}, onComplete, onSkip, onDismiss }) {
  const [msgs, setMsgs] = useState([]);          // [{role:'bot'|'user', text}]
  const [action, setAction] = useState(null);    // current engine action
  const [text, setText] = useState('');
  const [picked, setPicked] = useState([]);      // priorities multi-select
  const [aName, setAName] = useState('');
  const [aIcon, setAIcon] = useState('');
  const [catalog, setCatalog] = useState({});
  const [asst, setAsst] = useState({ names: [], icons: [] });
  const [busy, setBusy] = useState(true);
  const [dontShow, setDontShow] = useState(false);
  const bodyRef = useRef(null);
  const startedRef = useRef(false);   // #64 fix: start the interview exactly once, even if the
                                      // parent re-renders and passes a fresh fetchWithAuth identity

  // Never reject: a transport failure or a non-JSON error body resolves to {} so `apply` can degrade.
  const post = (path, body) => fetchWithAuth(`${API_BASE}/api/interview/${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}),
  }).then(r => r.json().catch(() => ({}))).catch(() => ({}));

  const ENGINE_STATUSES = ['ask', 'clarify', 'confirm', 'redirect', 'skipped', 'committed', 'handoff', 'done'];

  // apply an engine action: push its bot message, land on the right input.
  // DEGRADE, NEVER DEAD-END: an error body (a 500's {detail:…}, or {} from the guard above) carries no
  // `status`, and this used to fall straight through — leaving the user staring at a conversation that had
  // silently stopped, with no message and no way forward. Anything unrecognized now says so and returns the
  // user to a usable input, so they can retry or skip.
  const apply = (a) => {
    if (!a || !ENGINE_STATUSES.includes(a.status)) {
      setAction({ status: 'error' });
      setMsgs(m => [...m, { role: 'bot', text: "Sorry — that didn't go through on my end. Try that once more, or use “Skip setup” and finish anytime in Settings." }]);
      return;
    }
    setAction(a);
    if (a.status === 'done') { onComplete && onComplete(); return; }
    if (a.message) setMsgs(m => [...m, { role: 'bot', text: a.message }]);
    setText(''); setPicked([]); setAName(''); setAIcon('');
  };

  useEffect(() => {
    if (startedRef.current) return;   // guard: never re-run start on a re-render (was duplicating questions)
    startedRef.current = true;
    let alive = true;
    (async () => {
      try {
        // THESE TWO MUST NEVER REJECT. They are DECORATION — the launcher catalog and the
        // assistant name/icon lists are only read by two later slots, and both already
        // default to empty. Before this guard neither had a `.catch()`, so ONE transient
        // failure rejected Promise.all, threw past `apply()` straight into `finally`, and
        // left the user on a permanently blank "0 of 8" panel: no bot message, no error, no
        // retry, no way forward until a manual reload. Observed on PROD 2026-10-08, eight
        // minutes after a PM2 restart — the first screen a new user ever sees.
        // `post()` above was already guarded ("Never reject"); these were missed.
        const [lr, pr] = await Promise.all([
          fetchWithAuth(`${API_BASE}/api/dashboard/launcher`)
            .then(r => r.ok ? r.json() : {}).catch(() => ({})),
          fetchWithAuth(`${API_BASE}/api/support/personalize`)
            .then(r => r.ok ? r.json() : {}).catch(() => ({})),
        ]);
        if (!alive) return;
        setCatalog(lr.catalog || {});
        setAsst({ names: pr.available_names || [], icons: pr.available_icons || [] });
        const a = await post('start', { consumer: 'onboarding' });
        if (alive) apply(a);
      } catch (e) {
        // BELT AND BRACES. Any throw on this path — including one added later — must still
        // land the user on the degrade message with a usable input rather than a blank panel.
        // `apply(null)` takes the "unrecognized action" branch, which does exactly that.
        if (alive) apply(null);
      } finally { if (alive) setBusy(false); }
    })();
    return () => { alive = false; };
  }, [fetchWithAuth]);

  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [msgs, action]);

  const send = async (userText, displayText) => {
    setMsgs(m => [...m, { role: 'user', text: displayText || userText }]);
    setBusy(true);
    try { apply(await post('answer', { text: userText })); } finally { setBusy(false); }
  };
  const confirm = async (accept) => {
    setMsgs(m => [...m, { role: 'user', text: accept ? "Yes, that's right" : 'Let me fix that' }]);
    setBusy(true);
    try { apply(await post('confirm', { accept })); } finally { setBusy(false); }
  };
  const skip = async () => { await post('skip', { consumer: 'onboarding' }); if (dontShow) await post('dismiss', {}); (dontShow ? onDismiss : onSkip)?.(); };

  const slot = action?.slot;
  const isConfirm = action?.status === 'confirm';
  const isHandoff = action?.status === 'handoff';

  // ---- rendered input for the current step ----
  const input = () => {
    if (busy || !action || action.status === 'done') return null;
    if (isHandoff) return <button style={btn(GOLD, '#20180a')} onClick={() => onComplete?.()}>Finish in Settings</button>;
    if (isConfirm) {
      const v = action.values || {};
      return (
        <div>
          <div style={{ background: '#fff', border: `1px solid #e4e9f1`, borderLeft: `3px solid ${GOLD}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10, fontSize: 14 }}>
            <div style={{ color: '#6b7f9c', fontSize: 12, marginBottom: 6 }}>I understood:</div>
            {Object.entries(v).map(([k, val]) => (
              <div key={k}><span style={{ color: '#6b7f9c' }}>{k}:</span> <b style={{ color: NAVY }}>{Array.isArray(val) ? val.join(', ') : (typeof val === 'object' ? JSON.stringify(val) : String(val))}</b></div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 9 }}>
            <button style={btn(NAVY2, '#fff')} onClick={() => confirm(true)}>Yes, that's right</button>
            <button style={ghost()} onClick={() => confirm(false)}>Let me fix that</button>
          </div>
        </div>
      );
    }
    if (slot === 'priorities') {
      const keys = Object.keys(catalog);
      return (
        <div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            {keys.map(k => {
              const on = picked.includes(k);
              return <button key={k} onClick={() => setPicked(on ? picked.filter(x => x !== k) : [...picked, k])}
                style={{ ...chip(on) }}>{catalog[k].icon} {catalog[k].label}</button>;
            })}
          </div>
          <button style={btn(GOLD, '#20180a')} disabled={!picked.length}
            onClick={() => send(picked.join(', '), `Front-and-center: ${picked.map(k => catalog[k]?.label || k).join(', ')}`)}>
            Set up my dashboard
          </button>
        </div>
      );
    }
    if (slot === 'assistant_setup') {
      return (
        <div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            {asst.names.map(n => <button key={n} onClick={() => setAName(n)} style={chip(aName === n)}>{n}</button>)}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            {asst.icons.map(ic => <button key={ic} onClick={() => setAIcon(ic)} style={{ ...chip(aIcon === ic), fontSize: 20, padding: '6px 10px' }}>{ic}</button>)}
          </div>
          <button style={btn(NAVY2, '#fff')} disabled={!aName || !aIcon}
            onClick={() => send(`Call me ${aName} with the ${aIcon} avatar`, `Call you ${aName} ${aIcon}`)}>Finish</button>
        </div>
      );
    }
    // default: free text
    return (
      <div style={{ display: 'flex', gap: 9 }}>
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Type your answer…"
          onKeyDown={e => { if (e.key === 'Enter' && text.trim()) send(text.trim()); }}
          style={{ flex: 1, border: '1px solid #cfd9e8', borderRadius: 12, padding: '11px 14px', fontSize: 14 }} />
        <button style={btn(NAVY2, '#fff')} disabled={!text.trim()} onClick={() => send(text.trim())}>Send</button>
      </div>
    );
  };

  const p = action?.progress || offer?.progress || { done: 0, total: 8 };
  const pct = p.total ? Math.round((p.done / p.total) * 100) : 0;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: `radial-gradient(1200px 800px at 50% -10%, #1c3050 0%, ${NAVY} 55%, #0b1523 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: 560, maxWidth: '100%', height: 660, maxHeight: '90vh', background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 24px 70px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: `linear-gradient(135deg, ${NAVY2}, ${NAVY})`, color: '#fff', padding: '18px 22px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 700 }}>✦ Healist · Welcome</div>
            <span onClick={skip} style={{ color: '#a9bcd6', fontSize: 13, cursor: 'pointer' }}>Skip setup</span>
          </div>
          <div style={{ height: 6, borderRadius: 99, background: 'rgba(255,255,255,.15)', marginTop: 14, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${GOLD}, #e6cf86)`, transition: 'width .4s' }} />
          </div>
          <div style={{ fontSize: 11.5, color: '#9fb2cd', marginTop: 7 }}>
            {offer?.is_final ? 'Final setup — ' : ''}{offer?.resuming ? 'Picking up where you left off · ' : ''}{p.done} of {p.total}
          </div>
        </div>

        <div ref={bodyRef} style={{ flex: 1, overflowY: 'auto', padding: 22, background: '#fbfcfe', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {msgs.map((m, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: m.role === 'user' ? 'row-reverse' : 'row', gap: 10 }}>
              <div style={{ width: 30, height: 30, borderRadius: 9, flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, background: m.role === 'user' ? GOLD_SOFT : `linear-gradient(135deg, #1e3a5f, ${NAVY2})`, color: m.role === 'user' ? '#8a6d16' : '#fff' }}>{m.role === 'user' ? '🙂' : '✦'}</div>
              <div style={{ maxWidth: '82%', padding: '12px 15px', borderRadius: 16, fontSize: 14.5, lineHeight: 1.5, background: m.role === 'user' ? '#eef4ff' : '#f4f7fb', border: `1px solid ${m.role === 'user' ? '#d9e6fb' : '#e4e9f1'}`, color: m.role === 'user' ? '#12233d' : '#1a2436' }}>{m.text}</div>
            </div>
          ))}
          {/* A 13px grey ellipsis on an otherwise empty 660px panel reads as a broken page, not
              as loading — and this is the FIRST screen a new user sees. While the opening
              question is in flight there are no messages yet, so say plainly what is happening. */}
          {busy && msgs.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                          justifyContent: 'center', gap: 12, flex: 1, color: '#6b7f9c' }}>
              <div style={{ width: 26, height: 26, borderRadius: '50%',
                            border: `3px solid #e4e9f1`, borderTopColor: NAVY2,
                            animation: 'healistSpin .9s linear infinite' }} />
              <div style={{ fontSize: 13.5 }}>Getting your first question ready…</div>
              <style>{'@keyframes healistSpin{to{transform:rotate(360deg)}}'}</style>
            </div>
          )}
          {busy && msgs.length > 0 && (
            <div style={{ color: '#8496af', fontSize: 13, textAlign: 'center' }}>…</div>
          )}
        </div>

        <div style={{ borderTop: '1px solid #e4e9f1', padding: '14px 18px', background: '#fff' }}>
          {input()}
          {!isConfirm && action?.status !== 'done' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, justifyContent: 'center', fontSize: 12, color: '#6b7f9c', marginTop: 10 }}>
              <input type="checkbox" checked={dontShow} onChange={e => setDontShow(e.target.checked)} /> Don't show this again
            </label>
          )}
        </div>
      </div>
    </div>
  );
}

const btn = (bg, color) => ({ border: 0, borderRadius: 12, padding: '11px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer', background: bg, color });
const ghost = () => ({ border: '1px solid #cfd9e8', borderRadius: 12, padding: '11px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer', background: '#fff', color: '#2a3c56' });
const chip = (on) => ({ border: `1px solid ${on ? NAVY : '#cfd9e8'}`, background: on ? NAVY : '#fff', color: on ? '#fff' : '#22344c', fontSize: 13, fontWeight: 600, padding: '8px 13px', borderRadius: 999, cursor: 'pointer' });
